import i18n from "i18next";
import type { BackendModule, ReadCallback } from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Eager-load common namespace for each language
import enCommon from "./locales/en/common.json";
import esCommon from "./locales/es/common.json";
import frCommon from "./locales/fr/common.json";

const SUPPORTED_LANGUAGES = ["en", "es", "fr"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Custom i18next backend that lazy-loads namespace JSON via Vite dynamic import.
 * Only loads the requested language + namespace on demand (not all languages upfront).
 * i18next manages the loading state so useTranslation() waits for the bundle.
 */
const LazyBackend: BackendModule = {
  type: "backend",
  init() {},
  read(language: string, namespace: string, callback: ReadCallback) {
    if (namespace === "common") {
      // common is pre-bundled via resources — return empty so it merges cleanly
      callback(null, {});
      return;
    }
    import(`./locales/${language}/${namespace}.json`)
      .then((mod) => callback(null, mod.default ?? mod))
      .catch((err) => {
        console.warn(`[i18n] Failed to load ${language}/${namespace}:`, err);
        callback(null, {});
      });
  },
};

i18n
  .use(LazyBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Pre-bundled common namespaces
    resources: {
      en: { common: enCommon },
      es: { common: esCommon },
      fr: { common: frCommon },
    },

    supportedLngs: [...SUPPORTED_LANGUAGES],
    fallbackLng: "en",
    defaultNS: "common",
    ns: ["common"],

    // Language detection chain:
    // 1. ?lang=es URL param (QR code forcing)
    // 2. localStorage wondermaps_lang (user's previous pick)
    // 3. navigator.language
    // 4. Fallback: "en"
    detection: {
      order: ["querystring", "localStorage", "navigator"],
      lookupQuerystring: "lang",
      lookupLocalStorage: "wondermaps_lang",
      caches: ["localStorage"],
      convertDetectedLanguage: (lng: string) => {
        const base = lng.split("-")[0];
        if ((SUPPORTED_LANGUAGES as readonly string[]).includes(base)) {
          return base;
        }
        return "en";
      },
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },

    // common is pre-bundled, other namespaces loaded via LazyBackend
    partialBundledLanguages: true,

    react: {
      useSuspense: false,
    },
  });

export default i18n;
